import { flow /*, observable, computed, action */ } from "mobx"
import { TraxisCpeIdMixin } from "store/api/mixin/TraxisCpeIdMixin"
// MTVW-585: no retries
import { serviceUrls } from "store/qlapi/ServiceUrls"
//import { list, object, serializable, deserialize } from "serializr"
import moment from "moment"
import { getQlHeaders } from "store/qlapi/QlHeaders"
import { appStats } from "utils/TestSupport"
import { sleep } from "utils/Utils"

import { getCoCoModule, CoCoError } from "store/page/repo/CoCoModule"
const CoCo = await getCoCoModule()

export const analyticsEvent = {
	screenView: "screenView",
	discoverCategoryTap: "discoverCategoryTap",
	recordings: {tapEvent: "searchRecordingTab"},
	movies: {tapEvent: "searchMoviesTab"},
	series: {tapEvent: "searchSeriesTab"},
	genres: {tapEvent: "searchGenresTab"},
	actors: {tapEvent: "searchPersonTab"},
	sports: {tapEvent: "searchSportTab"},
	channels: {tapEvent: "searchChannelTab"},
	top: {tapEvent: "searchTopResultTab"},
	replayFilter: "replayFilter",
	recordingsFilter:	"recordingsFilter",
	upcomingFilter: "upcomingFilter",
	allFilter: "allFilter",
	radioStart: "radioStart",
	playerControlTap: "playerControlTap",
}

export const analyticsMeta = {
	screen: "screen",
	source: "source",
	channel: "channel",
	event: "event",
	isRecording: "isRecording",
	genre: "genre",
	person: "person",
	series: "series",
	subCat: "subCat",
	searchItem: "searchItem",
	seriesName: "seriesName",
	radioOrigin: "radioOrigin",
	radioStation: "radioStation",
	control: "control",
	player: "player",
	mediaType: "mediaType",
	nextEpisode: "nextEpisode",
	live: "live",
	replay: "replay",
	recording: "recording",
}

export const analyticsScreen = {
	login: "login",
	discover: "discover",
	discoverCategory: "discoverCategory",
	liveTv: "liveTv",
	tvGuide: "tvGuide",
	recordings: "recordings",
	radio: "radio",
	radioRecent: "radioRecent",
	radioGenres: "radioGenres",
	radioLanguages: "radioLanguages",
	radioSearch: "radioSearch",
	singleRecordings: "singleRecordings",
	seriesRecordings: "seriesRecordings",
	scheduledRecordings: "scheduledRecordings",
	archivedRecordings: "archivedRecordings",
	recordingEpisodes: "recordingEpisodes",
	newRecordings: "newRecordings",
	search: "search",
	settings: "settings",
	programDetails: "programDetails",
	channelListSelector: "channelListSelector",
	channelListEditor: "channelListEditor",
	genreRelatedPrograms: "genreRelatedPrograms",
	miniEpg: "miniEpg",
	personRelatedPrograms: "personRelatedPrograms",
	seriesRelatedPrograms: "seriesRelatedPrograms",
	programDetails: "programDetails",
	videoPlayer: "videoPlayer"
}

export const analyticsProgramDetailsSource = {
	//liveTvInsite: "liveTv (insite)",
	//liveTvDialog: "liveTv (dialog)",
	liveTv: "liveTv",
	//castController(info button)
	//programDetails: "programDetails (related event section in the detail page)",
	programDetails: "programDetails",
	discoverHero: "discoverHero",
	discoverLane: "discoverLane",
	discoverCategory: "discoverCategory",
	discoverCategoryHero: "discoverCategoryHero",
	miniEpg: "miniEpg",
	recordings: "recordings",
	singleRecordings: "singleRecordings",
	seriesRecordings: "seriesRecordings",
	scheduledRecordings: "scheduledRecordings",
	archivedRecordings: "archivedRecordings",
	recordingEpisodes: "recordingEpisodes",
	newRecordings: "newRecordings",
	seriesEpisodes: "seriesEpisodes",
	search: "search",
	personRelatedPrograms: "personRelatedPrograms",
	genreRelatedPrograms: "genreRelatedPrograms",
	seriesRelatedPrograms: "seriesRelatedPrograms",
	tvGuide: "tvGuide",
	recommendations: "recommendations",
	seriesCarousel: "seriesCarousel",
	//videoPlayer: "videoPlayer"
}

const STD_HEADERS = {
	'Accept': 'application/json',
	// Would cause CORS in keepAlive
	//'pragma': 'no-cache', 'cache-control': 'no-cache'
}

export const logLevel = CoCo.LogLevel

export class LogService /*extends TraxisCpeIdMixin*/ {
	// Guard property to track if the flush loop is already running
   isFlushing = false

	get xHeaders() {
		// make a copy of STD_HEADERS, otherwise STD_HEADERS would be changed!
		const headers = {}
		Object.assign(headers, STD_HEADERS)
		Object.assign(headers, getQlHeaders())
		return headers
	}

	version = flow(function* () {
		let resultJson = yield CoCo.apiFetchLogVersionRaw()
		// CoCo returns string instead of object, need to convert to object
		if (typeof resultJson === "string") resultJson = JSON.parse(resultJson)
		return `${resultJson["build_tag"]} (${resultJson["build_date"]})`
	})

	versionUrl() {
		return [serviceUrls.logUrl + "version", this.xHeaders]
	}

	flushLogs = flow(function* () {
		// Early exit if a flush operation is already active
		if (this.isFlushing) return

		// Lock the gate
		this.isFlushing = true

		try {
			 yield sleep(1000)
			 let remainingCount = yield CoCo.flushLogs()
			 yield sleep(1000)

			 while (remainingCount > 0) {
				  remainingCount = yield CoCo.flushLogs()
				  yield sleep(1000)
			 }
		} finally {
			 // Always unlock the gate, even if an unexpected error occurs
			 this.isFlushing = false
		}
  })

	/**
	* Resolves the correct CoCo logging method based on the log level.
	* Returns the invocation promise/value to be yielded directly by a MobX flow.
	*/
	logWithCoCo(level, tag, logEvent, metaStr) {
		// Get string name (handles both number and string)
		const levelStr = typeof level === 'number' ? logLevel[level] : level
		
		// Instead of null, return a resolved promise so 'yield' passes cleanly
		if (!levelStr || levelStr === 'Off') return Promise.resolve()

		// Dynamically resolve the method name (e.g., "logInfo")
		const methodName = `log${levelStr}`

		// Return the execution call directly to the flow
		if (typeof CoCo[methodName] === 'function') {
			const result = CoCo[methodName](tag, logEvent, metaStr)
			//console.debug("methodName", CoCo[methodName], tag, logEvent, metaStr)
			return result
		}

		// Fallback to logInfo if a specific logger level doesn't exist
		return CoCo.logInfo(tag, `[Fallback - ${levelStr}] ${logEvent}`, metaStr)
	}

	logAsync = flow(function* (logLevel, logEvent, tag = "", meta = null) {
		try {
			if (!meta) meta = {}
			meta.client = "web"
			const metaStr =  meta ? JSON.stringify(meta) : null
			console.debug("logAsync %o", {tag, logEvent, meta, metaStr})
			yield this.logWithCoCo(logLevel, tag, logEvent, metaStr)
			/*yield*/ this.flushLogs()
		}
		catch (e) {
			console.error("logAsync %o", e)
		}
	})

	analyticsAsync = flow(function* (name, duration, meta = null) {
		try {
			if (!meta) meta = {}
			meta.client = "web"
			const metaStr =  meta ? JSON.stringify(meta) : null
			console.debug("analyticsAsync %o", {name, duration, meta, metaStr})
			const msg = new CoCo.AnalyticsMessage(name, duration ? BigInt(duration) : null, metaStr)
			yield CoCo.writeAnalyticsMessage(msg)
			/*yield*/ this.flushLogs()
		}
		catch (e) {
			console.error("analyticsAsync %o", e)
		}
	})

	analyticsScreenview = flow(function* (meta) {
		yield this.analyticsAsync(analyticsEvent.screenView, null, meta)
	})

	analyticsDiscoverCategoryTap = flow(function* (meta) {
		yield this.analyticsAsync(analyticsEvent.discoverCategoryTap, null, meta)
	})

	analyticsTapEvent = flow(function* (tapEvent, meta) {
		yield this.analyticsAsync(tapEvent, null, meta)
	})

	analyticsRadioStart = flow(function* (meta) {
		//console.debug("analyticsRadioStart", analyticsEvent.radioStart, meta)
		yield this.analyticsAsync(analyticsEvent.radioStart, null, meta)
	})

	analyticsPlayerControlTap = flow(function* (meta) {
		//console.debug("analyticsPlayerControlTap", analyticsEvent.playerControlTap, meta)
		yield this.analyticsAsync(analyticsEvent.playerControlTap, null, meta)
	})
}
